import { Close as CloseIcon, ShoppingCart as CartIcon } from '@mui/icons-material';
import { Drawer, Box, Typography, IconButton, Divider, Alert } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

// Import cart components
import { CartItem, CartEmptyState, CartLoadingState, CartCheckoutSection, CommissionAttributionSection } from './cart';
import { ServiceDetailModal, VariantEditModal } from './cart';
import { useCart } from '../../contexts/CartContext';
import { ApiClient } from '../../lib/api/client';

interface ShoppingCartProps {
  open: boolean;
  onClose: () => void;
  onCheckout?: () => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = ({ open, onClose, onCheckout }) => {
  const router = useRouter();
  const { cart, loading, error, updateCartItem, removeCartItem, saveForLater, moveToCart, toggleItemSelection } = useCart();

  // Modal states
  const [serviceDetailModal, setServiceDetailModal] = useState({
    open: false,
    service: null,
    loading: false,
    cartItem: null as any
  });

  const [variantEditModal, setVariantEditModal] = useState({
    open: false,
    service: null as any,
    cartItem: null as any,
    selectedVariantKey: '',
    additionalUsers: 0,
    recurringBoltOns: [] as Array<{ addon_key: string }>,
    saving: false,
  });

  // Handle cart item actions
  const handleUpdateQuantity = async (itemId: string, quantity: number) => {
    try {
      await updateCartItem(itemId, { quantity });
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    try {
      await removeCartItem(itemId);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleSaveForLater = async (itemId: string) => {
    try {
      await saveForLater(itemId);
    } catch (error) {
      console.error('Failed to save for later:', error);
    }
  };

  const handleMoveToCart = async (itemId: string) => {
    try {
      await moveToCart(itemId);
    } catch (error) {
      console.error('Failed to move to cart:', error);
    }
  };

  const handleToggleSelection = async (itemId: string, selected: boolean) => {
    try {
      await toggleItemSelection(itemId, selected);
    } catch (error) {
      console.error('Failed to toggle selection:', error);
    }
  };

  const handleViewDetails = (serviceId: string) => {
    router.push(`/services/${serviceId}`);
  };

  const handleEditVariants = async (serviceId: string, item: any) => {
    setVariantEditModal({
      open: true,
      service: null,
      cartItem: item,
      selectedVariantKey: item.variant_key || '',
      additionalUsers: Number(item.service_configuration?.additional_users || 0),
      recurringBoltOns: Array.isArray(item.service_configuration?.recurring_bolt_ons)
        ? item.service_configuration.recurring_bolt_ons
        : [],
      saving: false,
    });

    try {
      const serviceData = await ApiClient.get(`/api/marketplace/services/${serviceId}`);
      setVariantEditModal((prev) => ({
        ...prev,
        service: serviceData,
        selectedVariantKey: prev.selectedVariantKey || serviceData.default_variant_key || '',
      }));
    } catch (error) {
      console.error('Failed to load service variants:', error);
      setVariantEditModal({
        open: false,
        service: null,
        cartItem: null,
        selectedVariantKey: '',
        additionalUsers: 0,
        recurringBoltOns: [],
        saving: false,
      });
    }
  };

  const handleCloseVariantEditModal = () => {
    if (variantEditModal.saving) return;

    setVariantEditModal({
      open: false,
      service: null,
      cartItem: null,
      selectedVariantKey: '',
      additionalUsers: 0,
      recurringBoltOns: [],
      saving: false,
    });
  };

  const handleUpdateVariants = async () => {
    if (!variantEditModal.cartItem || !variantEditModal.selectedVariantKey) return;

    try {
      setVariantEditModal((prev) => ({ ...prev, saving: true }));

      const nextConfiguration = { ...(variantEditModal.cartItem.service_configuration || {}) };
      const normalizedAdditionalUsers = Math.max(0, Math.floor(Number(variantEditModal.additionalUsers) || 0));

      if (normalizedAdditionalUsers > 0) {
        nextConfiguration.additional_users = normalizedAdditionalUsers;
      } else {
        delete nextConfiguration.additional_users;
      }

      if (variantEditModal.recurringBoltOns.length > 0) {
        nextConfiguration.recurring_bolt_ons = variantEditModal.recurringBoltOns;
      } else {
        delete nextConfiguration.recurring_bolt_ons;
      }

      await updateCartItem(variantEditModal.cartItem.id, {
        variant_key: variantEditModal.selectedVariantKey,
        service_configuration: nextConfiguration,
      });

      handleCloseVariantEditModal();
    } catch (error) {
      console.error('Failed to update cart configuration:', error);
      setVariantEditModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    } else {
      // Default checkout behavior - navigate to checkout page (catalog-aware)
      const slug = router.query.catalogInstance as string | undefined;
      router.push(slug ? `/c/${slug}/checkout` : '/checkout');
    }
  };

  const cartItems = Array.isArray(cart?.items) ? cart.items : [];
  const readyItems = Array.isArray(cart?.ready_items) ? cart.ready_items : [];
  const pendingItems = Array.isArray(cart?.pending_items) ? cart.pending_items : [];
  const savedForLaterItems = Array.isArray(cart?.saved_for_later_items)
    ? cart.saved_for_later_items
    : [];

  const getReadyItemsCount = () => readyItems.length;
  const getTotalItems = () => (typeof cart?.total_items === 'number' ? cart.total_items : cartItems.length);
  const canCheckout = getReadyItemsCount() > 0;

  if (loading) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <CartLoadingState />
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 500 },
            p: 0
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon />
              Shopping Cart ({getTotalItems()})
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Error Display */}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Cart Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {!cart || cartItems.length === 0 ? (
              <CartEmptyState />
            ) : (
              <Box sx={{ p: 2 }}>
                {/* Ready Items */}
                {readyItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Ready for Checkout ({readyItems.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {readyItems.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          onUpdateQuantity={handleUpdateQuantity}
                          onRemove={handleRemoveItem}
                          onSaveForLater={handleSaveForLater}
                          onMoveToCart={handleMoveToCart}
                          onToggleSelection={handleToggleSelection}
                          onViewDetails={handleViewDetails}
                          onEditVariants={handleEditVariants}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Pending Items */}
                {pendingItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'warning.main' }}>
                      Pending Items ({pendingItems.length})
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      These items need attention before checkout.
                    </Alert>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {pendingItems.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          onUpdateQuantity={handleUpdateQuantity}
                          onRemove={handleRemoveItem}
                          onSaveForLater={handleSaveForLater}
                          onMoveToCart={handleMoveToCart}
                          onToggleSelection={handleToggleSelection}
                          onViewDetails={handleViewDetails}
                          onEditVariants={handleEditVariants}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Saved for Later */}
                {savedForLaterItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary' }}>
                      Saved for Later ({savedForLaterItems.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {savedForLaterItems.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          onUpdateQuantity={handleUpdateQuantity}
                          onRemove={handleRemoveItem}
                          onSaveForLater={handleSaveForLater}
                          onMoveToCart={handleMoveToCart}
                          onToggleSelection={handleToggleSelection}
                          onViewDetails={handleViewDetails}
                          onEditVariants={handleEditVariants}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Commission Attribution */}
                {cart.attribution_breakdown && Object.keys(cart.attribution_breakdown).length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <CommissionAttributionSection
                      cart={cart as any}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Checkout Section */}
          {cart && cartItems.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <CartCheckoutSection
                  cart={cart as any}
                  onCheckout={handleCheckout}
                  canCheckout={canCheckout}
                  getReadyItemsCount={getReadyItemsCount}
                  getTotalItems={getTotalItems}
                />
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Service Detail Modal */}
      <ServiceDetailModal
        open={serviceDetailModal.open}
        service={serviceDetailModal.service}
        loading={serviceDetailModal.loading}
        cartItem={serviceDetailModal.cartItem}
        onClose={() => setServiceDetailModal({ open: false, service: null, loading: false, cartItem: null })}
      />

      {/* Variant Edit Modal */}
      <VariantEditModal
        open={variantEditModal.open}
        service={variantEditModal.service}
        cartItem={variantEditModal.cartItem}
        selectedVariantKey={variantEditModal.selectedVariantKey}
        additionalUsers={variantEditModal.additionalUsers}
        recurringBoltOns={variantEditModal.recurringBoltOns}
        saving={variantEditModal.saving}
        onClose={handleCloseVariantEditModal}
        onSave={handleUpdateVariants}
        onVariantChange={(variantKey) => {
          setVariantEditModal((prev) => ({
            ...prev,
            selectedVariantKey: variantKey,
          }));
        }}
        onAdditionalUsersChange={(value) => {
          setVariantEditModal((prev) => ({
            ...prev,
            additionalUsers: Math.max(0, Math.floor(Number(value) || 0)),
          }));
        }}
        onRecurringBoltOnsChange={(value) => {
          setVariantEditModal((prev) => ({
            ...prev,
            recurringBoltOns: value,
          }));
        }}
      />
    </>
  );
};

export default ShoppingCart;
